// Transaction logs › Flat rate (wave 3, W2 look; admin and finance).
//
// Reads ONLY the plan's own nodes, exactly as before:
//   /flat_rate_drivers              enrollment roster (trigger-maintained)
//   /driver_daily_stats/{today}     worked-today markers written at settlement
//   /flat_rate_deductions/{day}     the nightly sweep ledger, one node per day
// once when the tab opens and again on Refresh or when the number of days
// changes (tens of KB for the roster, one small node per day).
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { ref, get } from 'firebase/database';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { FirebaseContext } from 'common';
import DashboardCard from '../../components/DashboardCard';
import { tokens as c } from '../../theme';
import { formatAddisDateTime } from '../../common/exportUtils';
import { DataTable, ChipGroup, IconCircle, formatNumber } from '../../components/modern';

// Every sweep outcome, with the hue its tile and figures use.
const OUTCOMES = [
  { key: 'charged', label: ['flat_outcome_charged', 'Charged'], hue: 'success' },
  { key: 'skipped_inactive', label: ['flat_outcome_no_trips', 'Skipped · No trips'], hue: 'neutral' },
  { key: 'skipped_optin_midday', label: ['flat_outcome_join_day', 'Skipped · Join day'], hue: 'info' },
  { key: 'skipped_no_rate', label: ['flat_outcome_no_rate', 'Skipped · No rate'], hue: 'amber' },
  { key: 'skipped_no_user', label: ['flat_outcome_no_user', 'Skipped · No user'], hue: 'neutral' },
  { key: 'failed', label: ['failed', 'Failed'], hue: 'danger' },
];

// Addis Ababa calendar day (UTC+3, no DST). offsetDays 0 = today,
// 1 = yesterday (the latest completed sweep), etc.
const addisDateKey = (offsetDays) =>
  new Date(Date.now() + 3 * 3600 * 1000 - offsetDays * 24 * 3600 * 1000)
    .toISOString().slice(0, 10);

function Section({ icon, hue, title, action, children }) {
  return (
    <Paper component="section" sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, mb: 2 }}>
        <IconCircle icon={icon} hue={hue} size={34} />
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.4, color: c.INK, flex: '1 1 200px', minWidth: 0 }}>
          {title}
        </Typography>
        {action || null}
      </Box>
      {children}
    </Paper>
  );
}

export default function FlatRateMetrics({ settings }) {
  const { t, i18n } = useTranslation();
  const { database } = useContext(FirebaseContext);
  const [roster, setRoster] = useState(null);
  const [todayStats, setTodayStats] = useState(null);
  const [rangeDays, setRangeDays] = useState(7);
  const [ledgers, setLedgers] = useState({}); // dateKey -> ledger object
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(() => {
    if (!database) return;
    setLoading(true);
    const dayKeys = Array.from({ length: rangeDays }, (_, i) => addisDateKey(i + 1));
    Promise.all([
      get(ref(database, 'flat_rate_drivers')).catch(() => null),
      get(ref(database, `driver_daily_stats/${addisDateKey(0)}`)).catch(() => null),
      ...dayKeys.map((d) => get(ref(database, `flat_rate_deductions/${d}`)).catch(() => null)),
    ]).then(([rosterSnap, todaySnap, ...ledgerSnaps]) => {
      setRoster((rosterSnap && rosterSnap.val()) || {});
      setTodayStats((todaySnap && todaySnap.val()) || {});
      const byDay = {};
      dayKeys.forEach((d, i) => {
        byDay[d] = (ledgerSnaps[i] && ledgerSnaps[i].val()) || {};
      });
      setLedgers(byDay);
    }).finally(() => setLoading(false));
  }, [database, rangeDays]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Enrollment (from /flat_rate_drivers) ─────────────────────────
  const enrollment = useMemo(() => {
    const r = roster || {};
    let enrolled = 0;
    let priceable = 0;
    let missingCarType = 0;
    let leaving = 0;
    Object.keys(r).forEach((uid) => {
      if (uid.charAt(0) === '_') return;
      const e = r[uid];
      if (!e) return;
      enrolled++;
      if (e.left) leaving++;
      if (e.carType) priceable++; else missingCarType++;
    });
    const lastReconcile = r._meta && r._meta.lastRun ? r._meta.lastRun : null;
    return { enrolled, priceable, missingCarType, leaving, lastReconcile };
  }, [roster]);

  // ── Worked today (from /driver_daily_stats/{today}) ──────────────
  const today = useMemo(() => {
    const s = todayStats || {};
    let drivers = 0;
    let trips = 0;
    Object.keys(s).forEach((uid) => {
      drivers++;
      trips += parseInt(s[uid], 10) || 0;
    });
    return { drivers, trips };
  }, [todayStats]);

  // ── Sweep range (from /flat_rate_deductions) ─────────────────────
  const range = useMemo(() => {
    const totals = { chargedAmount: 0, processed: 0 };
    OUTCOMES.forEach((o) => { totals[o.key] = 0; });
    const perDay = Object.keys(ledgers).sort().reverse().map((day) => {
      const entries = ledgers[day] || {};
      const row = { day, processed: 0, chargedAmount: 0 };
      OUTCOMES.forEach((o) => { row[o.key] = 0; });
      Object.keys(entries).forEach((uid) => {
        const e = entries[uid];
        if (!e || !e.status) return;
        row.processed++;
        if (row[e.status] !== undefined) row[e.status]++;
        if (e.status === 'charged') row.chargedAmount += parseFloat(e.amount) || 0;
      });
      totals.processed += row.processed;
      totals.chargedAmount += row.chargedAmount;
      OUTCOMES.forEach((o) => { totals[o.key] += row[o.key]; });
      return row;
    });
    return { perDay, totals };
  }, [ledgers]);

  const symbol = settings?.symbol || '';
  const fmtAmount = (n) => {
    const s = (parseFloat(n) || 0).toFixed(2);
    return settings?.swipe_symbol === false ? `${symbol} ${s}` : `${s} ${symbol}`;
  };
  const tx = (pair, opts) => t(pair[0], { defaultValue: pair[1], ...(opts || {}) });
  const firstLoad = loading && roster === null;
  const tileGrid = { display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' } };

  const dayTitle = t('flat_col_day', { defaultValue: 'Sweep day (Addis)' });
  const columns = [
    { title: dayTitle, field: 'day', type: 'date', defaultSort: 'desc' },
    { title: t('processed', { defaultValue: 'Processed' }), field: 'processed', type: 'number' },
    {
      title: tx(OUTCOMES[0].label),
      field: 'charged',
      type: 'number',
      render: (row) => <Box component="span" sx={{ fontWeight: 700, color: c.SUCCESS_TEXT }}>{formatNumber(row.charged)}</Box>,
    },
    {
      title: t('flat_col_collected', { defaultValue: 'Collected' }),
      field: 'chargedAmount',
      type: 'money',
      render: (row) => <Box component="span" sx={{ fontWeight: 700, color: c.INK }}>{fmtAmount(row.chargedAmount)}</Box>,
      exportValue: (row) => (parseFloat(row.chargedAmount) || 0).toFixed(2),
    },
    { title: t('flat_col_no_trips', { defaultValue: 'No trips' }), field: 'skipped_inactive', type: 'number' },
    { title: t('flat_col_join_day', { defaultValue: 'Join day' }), field: 'skipped_optin_midday', type: 'number' },
    { title: t('flat_col_no_rate', { defaultValue: 'No rate' }), field: 'skipped_no_rate', type: 'number' },
    { title: t('flat_col_no_user', { defaultValue: 'No user' }), field: 'skipped_no_user', type: 'number' },
    {
      title: tx(OUTCOMES[5].label),
      field: 'failed',
      type: 'number',
      render: (row) => (
        <Box component="span" sx={{ color: row.failed > 0 ? c.DANGER_TEXT : c.INK, fontWeight: row.failed > 0 ? 700 : 400 }}>
          {formatNumber(row.failed)}
        </Box>
      ),
    },
  ];

  const sweepTitle = t('flat_sweep_title', { n: rangeDays, defaultValue: 'Nightly sweep · last {{n}} days' });

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* ── Enrollment ─────────────────────────────────────────────── */}
      <Section
        icon={<GroupsOutlinedIcon />}
        hue="success"
        title={t('flat_enrollment_title', { defaultValue: 'Plan enrollment' })}
        action={
          <Button variant="outlined" color="neutral" size="small" startIcon={<RefreshRoundedIcon />} disabled={loading} onClick={loadAll}>
            {t('refresh', { defaultValue: 'Refresh' })}
          </Button>
        }
      >
        <Box sx={tileGrid}>
          <DashboardCard icon={<EventAvailableOutlinedIcon />} hue="success" title={t('flat_enrolled', { defaultValue: 'Enrolled drivers' })} value={formatNumber(enrollment.enrolled)} caption={t('flat_enrolled_sub', { defaultValue: 'in the charge roster' })} loading={firstLoad} />
          <DashboardCard icon={<DirectionsCarOutlinedIcon />} hue="info" title={t('flat_priceable', { defaultValue: 'Priceable' })} value={formatNumber(enrollment.priceable)} caption={t('flat_priceable_sub', { defaultValue: 'car type has a rate' })} loading={firstLoad} />
          <DashboardCard icon={<ReportProblemOutlinedIcon />} hue="amber" title={t('flat_missing_car_type', { defaultValue: 'Missing car type' })} value={formatNumber(enrollment.missingCarType)} caption={t('flat_missing_car_type_sub', { defaultValue: 'never charged until fixed' })} loading={firstLoad} />
          <DashboardCard icon={<LogoutRoundedIcon />} hue="neutral" title={t('flat_leaving', { defaultValue: 'Leaving' })} value={formatNumber(enrollment.leaving)} caption={t('flat_leaving_sub', { defaultValue: 'final charge pending' })} loading={firstLoad} />
          <DashboardCard
            icon={<TaskAltRoundedIcon />}
            hue="success"
            title={t('flat_worked_today', { defaultValue: 'Worked today' })}
            value={formatNumber(today.drivers)}
            caption={t('flat_trips_settled', { count: today.trips, defaultValue_one: '{{count}} plan trip settled', defaultValue_other: '{{count}} plan trips settled', defaultValue: '{{count}} plan trips settled' })}
            loading={firstLoad}
          />
        </Box>
        {enrollment.lastReconcile ? (
          <Typography variant="body2" sx={{ mt: 1.5, color: c.TEXT_3 }}>
            {t('flat_roster_note', {
              when: formatAddisDateTime(enrollment.lastReconcile),
              defaultValue: 'Roster counts come from /flat_rate_drivers (kept up to date by a trigger); last full reconcile {{when}}. "Worked today" counts plan drivers with at least one settled trip this Addis day.',
            })}
          </Typography>
        ) : null}
      </Section>

      {/* ── Nightly sweep ──────────────────────────────────────────── */}
      <Section
        icon={<NightsStayOutlinedIcon />}
        hue="info"
        title={sweepTitle}
        action={
          <ChipGroup
            label={t('flat_days', { defaultValue: 'Days' })}
            value={rangeDays}
            onChange={setRangeDays}
            options={[7, 14, 30].map((n) => ({ value: n, label: t('flat_days_n', { n, defaultValue: '{{n}} days' }) }))}
          />
        }
      >
        <Box sx={{ ...tileGrid, mb: 2 }}>
          <DashboardCard
            icon={<PaymentsOutlinedIcon />}
            hue="success"
            title={t('flat_fees_collected', { defaultValue: 'Fees collected' })}
            value={fmtAmount(range.totals.chargedAmount)}
            caption={t('flat_charges', { count: range.totals.charged, defaultValue_one: '{{count}} charge', defaultValue_other: '{{count}} charges', defaultValue: '{{count}} charges' })}
            loading={loading}
          />
          <DashboardCard icon={<FactCheckOutlinedIcon />} hue="neutral" title={t('processed', { defaultValue: 'Processed' })} value={formatNumber(range.totals.processed)} caption={t('flat_driver_days', { defaultValue: 'driver-days' })} loading={loading} />
          {OUTCOMES.filter((o) => o.key !== 'charged').map((o) => (
            <DashboardCard key={o.key} hue={o.hue} title={tx(o.label)} value={formatNumber(range.totals[o.key])} loading={loading} />
          ))}
        </Box>
        <DataTable
          title={sweepTitle}
          rows={range.perDay}
          columns={columns}
          rowKey="day"
          loading={loading && range.perDay.length === 0}
          refreshing={loading && range.perDay.length > 0}
          search={false}
          paging={false}
          densityToggle={false}
          countText={t('flat_days_count', { count: range.perDay.length, defaultValue_one: '{{count}} day', defaultValue_other: '{{count}} days', defaultValue: '{{count}} days' })}
          prefsKey="flat_rate_sweep"
          exportCsv
          exportPrint
          exportFileName="flat_rate_sweep"
          exportTitle={sweepTitle}
          minWidth={860}
          maxHeight={null}
          sx={{ boxShadow: 'none' }}
        />
        <Typography variant="body2" sx={{ mt: 1.5, color: c.TEXT_3 }}>
          {t('flat_sweep_note', {
            defaultValue:
              'The sweep runs at 12:05 am Addis time and charges the previous day, so the newest row is yesterday. "Join day" = joined the plan during the day with no plan trips; "No rate" = the car type has no daily rate; "No trips" appears only while skipping inactive days is on. A day with no data was before the feature went live, or the sweep did not run (check the function logs if Failed is above 0).',
          })}
        </Typography>
      </Section>
    </Box>
  );
}
